/**
 * Treppan Academy - Course Editor
 * Admin interface for creating and editing courses, lessons, and exams
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  BookOpen, Video, FileText, FileImage, Clock, Award,
  ArrowLeft, Eye, Settings, X, Check, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const MODULES = [
  { value: 'lead_management', label: 'Lead Management' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'automation', label: 'Automation' },
  { value: 'communication', label: 'Communication' },
  { value: 'properties', label: 'Properties' },
  { value: 'general', label: 'General' }
];

const DIFFICULTIES = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const CONTENT_TYPES = [
  { value: 'text', label: 'Text/HTML', icon: FileText },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'pdf', label: 'PDF', icon: FileImage }
];

const LessonEditor = ({ lesson, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const ContentIcon = CONTENT_TYPES.find(t => t.value === lesson.content_type)?.icon || FileText;

  return (
    <div 
      className="rounded-xl transition-all"
      style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div 
        className="flex items-center gap-3 p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="h-5 w-5 cursor-move" style={{ color: colors.textMuted }} />
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${colors.gold}20` }}
        >
          <ContentIcon className="h-4 w-4" style={{ color: colors.gold }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate" style={{ color: colors.textHeading }}>
            {lesson.title || `Lesson ${index + 1}`}
          </p>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            {lesson.duration_minutes} min • {lesson.content_type}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
              data-testid={`lesson-${index}-move-up`}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          )}
          {!isLast && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
              data-testid={`lesson-${index}-move-down`}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-red-400 hover:text-red-300"
            data-testid={`lesson-${index}-delete`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: colors.border }}>
          <div className="pt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                Lesson Title
              </label>
              <Input
                value={lesson.title}
                onChange={(e) => onUpdate({ ...lesson, title: e.target.value })}
                placeholder="Enter lesson title"
                className="bg-transparent border-white/10"
                data-testid={`lesson-${index}-title`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                Duration (minutes)
              </label>
              <Input
                type="number"
                value={lesson.duration_minutes}
                onChange={(e) => onUpdate({ ...lesson, duration_minutes: parseInt(e.target.value) || 10 })}
                className="bg-transparent border-white/10"
                data-testid={`lesson-${index}-duration`}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              Description
            </label>
            <Input
              value={lesson.description || ''}
              onChange={(e) => onUpdate({ ...lesson, description: e.target.value })}
              placeholder="Brief description of this lesson"
              className="bg-transparent border-white/10"
              data-testid={`lesson-${index}-description`}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              Content Type
            </label>
            <Select 
              value={lesson.content_type} 
              onValueChange={(v) => onUpdate({ ...lesson, content_type: v })}
            >
              <SelectTrigger className="bg-transparent border-white/10" data-testid={`lesson-${index}-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {lesson.content_type === 'video' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                Video URL (YouTube, Vimeo, or direct link)
              </label>
              <Input
                value={lesson.video_url || ''}
                onChange={(e) => onUpdate({ ...lesson, video_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-transparent border-white/10"
                data-testid={`lesson-${index}-video-url`}
              />
            </div>
          )}
          
          {lesson.content_type === 'pdf' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                PDF URL
              </label>
              <Input
                value={lesson.pdf_url || ''}
                onChange={(e) => onUpdate({ ...lesson, pdf_url: e.target.value })}
                placeholder="https://example.com/document.pdf"
                className="bg-transparent border-white/10"
                data-testid={`lesson-${index}-pdf-url`}
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              {lesson.content_type === 'text' ? 'Content (HTML)' : 'Additional Notes (HTML)'}
            </label>
            <textarea
              value={lesson.content || ''}
              onChange={(e) => onUpdate({ ...lesson, content: e.target.value })}
              placeholder="<h2>Introduction</h2><p>Your content here...</p>"
              className="w-full h-32 px-3 py-2 rounded-lg bg-transparent resize-none"
              style={{ border: `1px solid ${colors.border}`, color: colors.textHeading }}
              data-testid={`lesson-${index}-content`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionEditor = ({ question, index, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div 
      className="rounded-xl transition-all"
      style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div 
        className="flex items-center gap-3 p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${colors.gold}20` }}
        >
          <span className="text-sm font-bold" style={{ color: colors.gold }}>{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate" style={{ color: colors.textHeading }}>
            {question.question || `Question ${index + 1}`}
          </p>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            {question.options?.length || 0} options • Answer: {(question.correct_answer ?? 0) + 1}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-red-400 hover:text-red-300"
            data-testid={`question-${index}-delete`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: colors.border }}>
          <div className="pt-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              Question
            </label>
            <textarea
              value={question.question}
              onChange={(e) => onUpdate({ ...question, question: e.target.value })}
              placeholder="Enter your question"
              className="w-full h-20 px-3 py-2 rounded-lg bg-transparent resize-none"
              style={{ border: `1px solid ${colors.border}`, color: colors.textHeading }}
              data-testid={`question-${index}-text`}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              Options
            </label>
            <div className="space-y-2">
              {(question.options || ['', '', '', '']).map((option, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    onClick={() => onUpdate({ ...question, correct_answer: i })}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      question.correct_answer === i ? 'ring-2' : ''
                    }`}
                    style={{ 
                      backgroundColor: question.correct_answer === i ? colors.success : colors.surfaceHover,
                      ringColor: colors.success
                    }}
                    data-testid={`question-${index}-correct-${i}`}
                  >
                    {question.correct_answer === i && <Check className="h-4 w-4 text-white" />}
                  </button>
                  <Input
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...(question.options || ['', '', '', ''])];
                      newOptions[i] = e.target.value;
                      onUpdate({ ...question, options: newOptions });
                    }}
                    placeholder={`Option ${i + 1}`}
                    className="flex-1 bg-transparent border-white/10"
                    data-testid={`question-${index}-option-${i}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
              Click the circle to mark the correct answer
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
              Explanation (shown after answering)
            </label>
            <Input
              value={question.explanation || ''}
              onChange={(e) => onUpdate({ ...question, explanation: e.target.value })}
              placeholder="Explain why this answer is correct"
              className="bg-transparent border-white/10"
              data-testid={`question-${index}-explanation`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isNew = !courseId;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  const [course, setCourse] = useState({
    title: '',
    description: '',
    module: 'general',
    difficulty: 'beginner',
    duration_hours: 1,
    thumbnail_url: '',
    tags: [],
    status: 'draft',
    lessons: [],
    exam: {
      title: '',
      description: '',
      passing_score: 70,
      time_limit_minutes: 30,
      questions: []
    }
  });

  useEffect(() => {
    if (courseId) {
      fetchCourse();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/courses/${courseId}?token=${token}`);
      const data = await res.json();
      if (data.success && data.course) {
        setCourse({
          ...data.course,
          exam: data.exam || {
            title: `${data.course.title} Exam`,
            description: '',
            passing_score: 70,
            time_limit_minutes: 30,
            questions: []
          }
        });
      }
    } catch (err) {
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!course.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    setSaving(true);
    try {
      const endpoint = isNew 
        ? `${API_URL}/api/academy/admin/courses?token=${token}`
        : `${API_URL}/api/academy/admin/courses/${courseId}?token=${token}`;
      
      const res = await fetch(endpoint, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(course)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(isNew ? 'Course created!' : 'Course saved!');
        if (isNew && data.course?.id) {
          navigate(`/academy/admin/courses/${data.course.id}/edit`);
        }
      } else {
        toast.error(data.error || 'Failed to save course');
      }
    } catch (err) {
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const addLesson = () => {
    setCourse(prev => ({
      ...prev,
      lessons: [
        ...prev.lessons,
        {
          id: `temp-${Date.now()}`,
          title: '',
          description: '',
          content_type: 'text',
          content: '',
          video_url: '',
          pdf_url: '',
          duration_minutes: 15,
          order: prev.lessons.length
        }
      ]
    }));
  };

  const updateLesson = (index, updatedLesson) => {
    setCourse(prev => ({
      ...prev,
      lessons: prev.lessons.map((l, i) => i === index ? updatedLesson : l)
    }));
  };

  const deleteLesson = (index) => {
    setCourse(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, i) => i !== index)
    }));
  };

  const moveLesson = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= course.lessons.length) return;
    
    setCourse(prev => {
      const lessons = [...prev.lessons];
      [lessons[index], lessons[newIndex]] = [lessons[newIndex], lessons[index]];
      return { ...prev, lessons: lessons.map((l, i) => ({ ...l, order: i })) };
    });
  };

  const addQuestion = () => {
    setCourse(prev => ({
      ...prev,
      exam: {
        ...prev.exam,
        questions: [
          ...prev.exam.questions,
          {
            id: `temp-${Date.now()}`,
            question: '',
            options: ['', '', '', ''],
            correct_answer: 0,
            explanation: ''
          }
        ]
      }
    }));
  };

  const updateQuestion = (index, updatedQuestion) => {
    setCourse(prev => ({
      ...prev,
      exam: {
        ...prev.exam,
        questions: prev.exam.questions.map((q, i) => i === index ? updatedQuestion : q)
      }
    }));
  };

  const deleteQuestion = (index) => {
    setCourse(prev => ({
      ...prev,
      exam: {
        ...prev.exam,
        questions: prev.exam.questions.filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.gold }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: colors.background }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/academy/admin/courses')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 
                className="text-2xl font-bold"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                {isNew ? 'Create New Course' : 'Edit Course'}
              </h1>
              {!isNew && (
                <Badge 
                  className="mt-1"
                  style={{ 
                    backgroundColor: course.status === 'published' ? `${colors.success}20` : `${colors.warning}20`,
                    color: course.status === 'published' ? colors.success : colors.warning
                  }}
                >
                  {course.status}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {!isNew && (
              <Button
                variant="outline"
                onClick={() => navigate(`/academy/courses/${courseId}`)}
                data-testid="preview-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: colors.gold, color: '#000' }}
              data-testid="save-course-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Course'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6" style={{ backgroundColor: colors.surface }}>
            <TabsTrigger value="details" data-testid="tab-details">
              <Settings className="h-4 w-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="lessons" data-testid="tab-lessons">
              <BookOpen className="h-4 w-4 mr-2" />
              Lessons ({course.lessons.length})
            </TabsTrigger>
            <TabsTrigger value="exam" data-testid="tab-exam">
              <Award className="h-4 w-4 mr-2" />
              Exam ({course.exam.questions.length})
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <CardHeader>
                <CardTitle style={{ color: colors.textHeading }}>Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Course Title *
                    </label>
                    <Input
                      value={course.title}
                      onChange={(e) => setCourse(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Lead Management Mastery"
                      className="bg-transparent border-white/10 text-lg"
                      data-testid="course-title"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Description *
                    </label>
                    <textarea
                      value={course.description}
                      onChange={(e) => setCourse(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what learners will achieve..."
                      className="w-full h-24 px-3 py-2 rounded-lg bg-transparent resize-none"
                      style={{ border: `1px solid ${colors.border}`, color: colors.textHeading }}
                      data-testid="course-description"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Module
                    </label>
                    <Select 
                      value={course.module} 
                      onValueChange={(v) => setCourse(prev => ({ ...prev, module: v }))}
                    >
                      <SelectTrigger className="bg-transparent border-white/10" data-testid="course-module">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODULES.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Difficulty
                    </label>
                    <Select 
                      value={course.difficulty} 
                      onValueChange={(v) => setCourse(prev => ({ ...prev, difficulty: v }))}
                    >
                      <SelectTrigger className="bg-transparent border-white/10" data-testid="course-difficulty">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIFFICULTIES.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Duration (hours)
                    </label>
                    <Input
                      type="number"
                      step="0.5"
                      value={course.duration_hours}
                      onChange={(e) => setCourse(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 1 }))}
                      className="bg-transparent border-white/10"
                      data-testid="course-duration"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Status
                    </label>
                    <Select 
                      value={course.status} 
                      onValueChange={(v) => setCourse(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger className="bg-transparent border-white/10" data-testid="course-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                      Thumbnail URL
                    </label>
                    <Input
                      value={course.thumbnail_url || ''}
                      onChange={(e) => setCourse(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                      className="bg-transparent border-white/10"
                      data-testid="course-thumbnail"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lessons Tab */}
          <TabsContent value="lessons">
            <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: colors.textHeading }}>
                  Course Lessons
                </CardTitle>
                <Button onClick={addLesson} data-testid="add-lesson-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Lesson
                </Button>
              </CardHeader>
              <CardContent>
                {course.lessons.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
                    <p style={{ color: colors.textMuted }}>No lessons yet</p>
                    <Button onClick={addLesson} className="mt-4" data-testid="add-first-lesson-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Lesson
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {course.lessons.map((lesson, index) => (
                      <LessonEditor
                        key={lesson.id}
                        lesson={lesson}
                        index={index}
                        onUpdate={(l) => updateLesson(index, l)}
                        onDelete={() => deleteLesson(index)}
                        onMoveUp={() => moveLesson(index, -1)}
                        onMoveDown={() => moveLesson(index, 1)}
                        isFirst={index === 0}
                        isLast={index === course.lessons.length - 1}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Exam Tab */}
          <TabsContent value="exam">
            <div className="space-y-6">
              <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <CardHeader>
                  <CardTitle style={{ color: colors.textHeading }}>Exam Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                        Exam Title
                      </label>
                      <Input
                        value={course.exam.title}
                        onChange={(e) => setCourse(prev => ({ 
                          ...prev, 
                          exam: { ...prev.exam, title: e.target.value }
                        }))}
                        placeholder="e.g., Lead Management Certification Exam"
                        className="bg-transparent border-white/10"
                        data-testid="exam-title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                        Passing Score (%)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={course.exam.passing_score}
                        onChange={(e) => setCourse(prev => ({ 
                          ...prev, 
                          exam: { ...prev.exam, passing_score: parseInt(e.target.value) || 70 }
                        }))}
                        className="bg-transparent border-white/10"
                        data-testid="exam-passing-score"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textBody }}>
                        Time Limit (minutes)
                      </label>
                      <Input
                        type="number"
                        min="5"
                        value={course.exam.time_limit_minutes}
                        onChange={(e) => setCourse(prev => ({ 
                          ...prev, 
                          exam: { ...prev.exam, time_limit_minutes: parseInt(e.target.value) || 30 }
                        }))}
                        className="bg-transparent border-white/10"
                        data-testid="exam-time-limit"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle style={{ color: colors.textHeading }}>
                    Exam Questions
                  </CardTitle>
                  <Button onClick={addQuestion} data-testid="add-question-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </CardHeader>
                <CardContent>
                  {course.exam.questions.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
                      <p style={{ color: colors.textMuted }}>No exam questions yet</p>
                      <Button onClick={addQuestion} className="mt-4" data-testid="add-first-question-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Your First Question
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {course.exam.questions.map((question, index) => (
                        <QuestionEditor
                          key={question.id || index}
                          question={question}
                          index={index}
                          onUpdate={(q) => updateQuestion(index, q)}
                          onDelete={() => deleteQuestion(index)}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
